import { supabase, QuestionnaireSession, isSupabaseConfigured } from '../lib/supabase';
import { logger } from '../utils/logger';
import { nanoid } from 'nanoid';

const SESSION_DATA_BACKUP_KEY = 'btoolme_session_backup';
const MAX_SAVE_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export class SessionSaveError extends Error {
  constructor(message: string, public readonly isRetryable: boolean = true) {
    super(message);
    this.name = 'SessionSaveError';
  }
}

interface SessionBackup {
  formData: Record<string, any>;
  currentStep: number;
  email?: string;
  name?: string;
  timestamp: number;
}

export class QuestionnaireSessionService {
  private static SESSION_ID_KEY = 'btoolme_session_id';

  static getSessionId(): string {
    let sessionId = localStorage.getItem(this.SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = nanoid(32);
      localStorage.setItem(this.SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }

  private static saveLocalBackup(data: SessionBackup): void {
    try {
      localStorage.setItem(SESSION_DATA_BACKUP_KEY, JSON.stringify(data));
      logger.debug('Session backup saved to localStorage');
    } catch (error) {
      logger.warn('Failed to save session backup to localStorage', error);
    }
  }

  private static getLocalBackup(): SessionBackup | null {
    try {
      const backup = localStorage.getItem(SESSION_DATA_BACKUP_KEY);
      if (!backup) return null;

      const parsed = JSON.parse(backup) as SessionBackup;
      const hourInMs = 60 * 60 * 1000;
      const maxAge = 24 * hourInMs;
      if (Date.now() - parsed.timestamp > maxAge) {
        logger.info('Session backup expired, removing');
        localStorage.removeItem(SESSION_DATA_BACKUP_KEY);
        return null;
      }

      return parsed;
    } catch (error) {
      logger.warn('Failed to read session backup from localStorage', error);
      return null;
    }
  }

  static clearLocalBackup(): void {
    try {
      localStorage.removeItem(SESSION_DATA_BACKUP_KEY);
    } catch (error) {
      logger.warn('Failed to clear session backup', error);
    }
  }

  static async createOrUpdateSession(data: {
    formData: Record<string, any>;
    currentStep: number;
    email?: string;
    name?: string;
  }): Promise<QuestionnaireSession | null> {
    this.saveLocalBackup({
      formData: data.formData,
      currentStep: data.currentStep,
      email: data.email,
      name: data.name,
      timestamp: Date.now(),
    });

    if (!isSupabaseConfigured() || !supabase) {
      logger.warn('Supabase not configured - session saved to localStorage only');
      return null;
    }

    try {
      const sessionId = this.getSessionId();

      const { data: existingSession, error: fetchError } = await supabase
        .from('questionnaire_sessions')
        .select('*')
        .eq('session_id', sessionId)
        .maybeSingle();

      if (fetchError) {
        logger.error('Error fetching session', fetchError);
        return null;
      }

      if (existingSession) {
        const { data: updatedSession, error: updateError } = await supabase
          .from('questionnaire_sessions')
          .update({
            form_data: data.formData,
            current_step: data.currentStep,
            email: data.email || existingSession.email,
            name: data.name || existingSession.name,
          })
          .eq('id', existingSession.id)
          .select()
          .maybeSingle();

        if (updateError) {
          logger.error('Error updating session', updateError);
          return null;
        }

        logger.info('Session updated', { sessionId, step: data.currentStep });
        return updatedSession;
      } else {
        const { data: newSession, error: insertError } = await supabase
          .from('questionnaire_sessions')
          .insert({
            session_id: sessionId,
            form_data: data.formData,
            current_step: data.currentStep,
            email: data.email,
            name: data.name,
          })
          .select()
          .maybeSingle();

        if (insertError) {
          logger.error('Error creating session', insertError);
          return null;
        }

        logger.info('Session created', { sessionId, step: data.currentStep });
        return newSession;
      }
    } catch (error) {
      logger.error('Error in createOrUpdateSession', error);
      return null;
    }
  }

  static async saveSessionMandatory(data: {
    formData: Record<string, any>;
    currentStep: number;
    email?: string;
    name?: string;
  }): Promise<QuestionnaireSession> {
    this.saveLocalBackup({
      formData: data.formData,
      currentStep: data.currentStep,
      email: data.email,
      name: data.name,
      timestamp: Date.now(),
    });

    if (!isSupabaseConfigured() || !supabase) {
      throw new SessionSaveError(
        'Database connection is not available. Please check your internet connection and try again.',
        false
      );
    }

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= MAX_SAVE_RETRIES; attempt++) {
      try {
        logger.info(`Attempting to save session (attempt ${attempt}/${MAX_SAVE_RETRIES})`);
        const sessionId = this.getSessionId();

        const { data: existingSession, error: fetchError } = await supabase
          .from('questionnaire_sessions')
          .select('*')
          .eq('session_id', sessionId)
          .maybeSingle();

        if (fetchError) {
          throw new Error(`Failed to check existing session: ${fetchError.message}`);
        }

        if (existingSession) {
          const { data: updatedSession, error: updateError } = await supabase
            .from('questionnaire_sessions')
            .update({
              form_data: data.formData,
              current_step: data.currentStep,
              email: data.email || existingSession.email,
              name: data.name || existingSession.name,
            })
            .eq('id', existingSession.id)
            .select()
            .maybeSingle();

          if (updateError) {
            throw new Error(`Failed to update session: ${updateError.message}`);
          }
          if (!updatedSession) {
            throw new Error('Session update returned no row');
          }

          logger.info('Session saved successfully', { sessionId, step: data.currentStep });
          return updatedSession;
        } else {
          const { data: newSession, error: insertError } = await supabase
            .from('questionnaire_sessions')
            .insert({
              session_id: sessionId,
              form_data: data.formData,
              current_step: data.currentStep,
              email: data.email,
              name: data.name,
            })
            .select()
            .maybeSingle();

          if (insertError) {
            throw new Error(`Failed to create session: ${insertError.message}`);
          }
          if (!newSession) {
            throw new Error('Session insert returned no row');
          }

          logger.info('Session created successfully', { sessionId, step: data.currentStep });
          return newSession;
        }
      } catch (error) {
        lastError = error as Error;
        logger.warn(`Session save attempt ${attempt} failed`, error);

        if (attempt < MAX_SAVE_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * attempt));
        }
      }
    }

    throw new SessionSaveError(
      lastError?.message || 'Failed to save your progress after multiple attempts. Please try again.',
      true
    );
  }

  static async completeSession(data: {
    formData: Record<string, any>;
    email: string;
    name: string;
    recommendations: any[];
  }): Promise<QuestionnaireSession | null> {
    if (!isSupabaseConfigured() || !supabase) {
      logger.warn('Supabase not configured - session completion not persisted');
      return null;
    }

    try {
      const sessionId = this.getSessionId();

      const { data: existingSession, error: fetchError } = await supabase
        .from('questionnaire_sessions')
        .select('*')
        .eq('session_id', sessionId)
        .maybeSingle();

      if (fetchError) {
        logger.error('Error fetching session for completion', fetchError);
        return null;
      }

      if (existingSession) {
        const { data: completedSession, error: updateError } = await supabase
          .from('questionnaire_sessions')
          .update({
            form_data: data.formData,
            email: data.email,
            name: data.name,
            recommendations: data.recommendations,
            completed: true,
            completed_at: new Date().toISOString(),
          })
          .eq('id', existingSession.id)
          .select()
          .maybeSingle();

        if (updateError) {
          logger.error('Error completing session', updateError);
          return null;
        }

        logger.info('Session completed', { sessionId, email: data.email });
        return completedSession;
      } else {
        const { data: newSession, error: insertError } = await supabase
          .from('questionnaire_sessions')
          .insert({
            session_id: sessionId,
            form_data: data.formData,
            email: data.email,
            name: data.name,
            recommendations: data.recommendations,
            completed: true,
            completed_at: new Date().toISOString(),
          })
          .select()
          .maybeSingle();

        if (insertError) {
          logger.error('Error creating completed session', insertError);
          return null;
        }

        logger.info('Session created and completed', { sessionId, email: data.email });
        return newSession;
      }
    } catch (error) {
      logger.error('Error in completeSession', error);
      return null;
    }
  }

  static async getSession(sessionId?: string): Promise<QuestionnaireSession | null> {
    const tryLocalBackup = (): QuestionnaireSession | null => {
      const backup = this.getLocalBackup();
      if (backup && backup.formData) {
        logger.info('Using localStorage backup for session data');
        return {
          id: 'local',
          session_id: this.getSessionId(),
          form_data: backup.formData,
          current_step: backup.currentStep,
          email: backup.email || null,
          name: backup.name || null,
          recommendations: null,
          completed: false,
          completed_at: null,
          created_at: new Date(backup.timestamp).toISOString(),
          updated_at: new Date(backup.timestamp).toISOString(),
          user_id: null,
          resume_token: '',
        } as QuestionnaireSession;
      }
      return null;
    };

    if (!isSupabaseConfigured() || !supabase) {
      logger.warn('Supabase not configured - trying localStorage backup');
      return tryLocalBackup();
    }

    try {
      const id = sessionId || this.getSessionId();

      const { data, error } = await supabase
        .from('questionnaire_sessions')
        .select('*')
        .eq('session_id', id)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching session from Supabase', error);
        return tryLocalBackup();
      }

      if (!data) {
        logger.info('No session found in Supabase, trying localStorage backup');
        return tryLocalBackup();
      }

      return data;
    } catch (error) {
      logger.error('Error in getSession', error);
      return tryLocalBackup();
    }
  }

  static async getSessionWithRetry(
    sessionId?: string,
    maxRetries: number = 3
  ): Promise<QuestionnaireSession | null> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const session = await this.getSession(sessionId);
        if (session) {
          return session;
        }

        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
        }
      } catch (error) {
        lastError = error as Error;
        logger.warn(`Session fetch attempt ${attempt} failed`, error);

        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
        }
      }
    }

    if (lastError) {
      logger.error('All session fetch attempts failed', lastError);
    }

    return null;
  }

  static async waitForPaymentVerification(
    sessionId: string,
    maxWaitMs: number = 30000,
    pollIntervalMs: number = 2000
  ): Promise<boolean> {
    if (!isSupabaseConfigured() || !supabase) {
      return false;
    }

    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitMs) {
      try {
        const { data, error } = await supabase
          .from('questionnaire_sessions')
          .select('completed')
          .eq('session_id', sessionId)
          .maybeSingle();

        if (!error && data?.completed) {
          return true;
        }
      } catch (err) {
        logger.warn('Error checking payment status', err);
      }

      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    }

    return false;
  }
}
